import {ReactElement, useState} from "react";
import {ThemeProvider} from "styled-components";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {Button} from "@zendeskgarden/react-buttons";
import {Checkbox, Field, Input, Label} from "@zendeskgarden/react-forms";
import {Inline} from "@zendeskgarden/react-loaders";
import {toast} from "react-toastify";
import {ViziblyTheme} from "../../../styles/zendesk-garden/ViziblyZDGTheme";
import {COLORS} from "../../../constants/Colors";
import {useRenameWorksheetLinesMutation} from "../../../__generated__/generated_types";


export interface RenameLineItemsModalProps {
    visible: boolean;
    currentName: string;
    lineItemIds: string[];
    budgetYear: number;
    onClose: () => void;
    onRenamed: () => void;
}

export function RenameLineItemsModal(props: RenameLineItemsModalProps): ReactElement {
    const {visible, currentName, lineItemIds, budgetYear, onClose, onRenamed} = props;

    const [newName, setNewName] = useState<string>("");
    const [skipOnConflict, setSkipOnConflict] = useState<boolean>(true);
    const [isRenaming, setIsRenaming] = useState<boolean>(false);

    const [renameWorksheetLines, {loading}] = useRenameWorksheetLinesMutation({
        notifyOnNetworkStatusChange: true,
    });

    const reset = () => {
        setNewName("");
        setSkipOnConflict(true);
        setIsRenaming(false);
        onClose();
    };

    const handleSubmit = async () => {
        if (!newName.trim()) {
            toast.error("Please enter a new name");
            return;
        }

        setIsRenaming(true);

        try {
            const result = await renameWorksheetLines({
                variables: {
                    ids: lineItemIds,
                    newName: newName.trim(),
                    budgetYear,
                    skipOnConflict,
                },
            });

            if (result.data?.renameWorksheetLines) {
                toast.success("Line items renamed successfully");
                onRenamed();
                reset();
            } else {
                toast.error("Failed to rename line items");
            }
        } catch (error) {
            toast.error("Error renaming line items");
        } finally {
            setIsRenaming(false);
        }
    };

    if (!visible) {
        return <></>;
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Modal onClose={reset}>
                <Header>Rename {lineItemIds.length} "{currentName}" Line Items</Header>
                <Body>
                    <Field>
                        <Label>New Name</Label>
                        <Input
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                        />
                    </Field>
                    <Field style={{marginTop: "0.75rem"}}>
                        <Checkbox
                            checked={skipOnConflict}
                            onChange={() => setSkipOnConflict(!skipOnConflict)}
                        >
                            <Label>Skip Renames that Would Cause Duplicates.</Label>
                        </Checkbox>
                    </Field>
                </Body>
                <Footer>
                    <FooterItem>
                        <Button onClick={reset} isBasic disabled={isRenaming || loading}>
                            Cancel
                        </Button>
                    </FooterItem>
                    <FooterItem>
                        <Button isPrimary onClick={handleSubmit} disabled={!newName.trim() || isRenaming || loading}>
                            {isRenaming
                                ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading"/>
                                : <span>Rename</span>
                            }
                        </Button>
                    </FooterItem>
                </Footer>
                <Close aria-label="Close modal"/>
            </Modal>
        </ThemeProvider>
    );
}
